import { getSabineSessionSource } from "@/lib/sabine-chat/sources";

// What a shared copy's panel says when the page gives it no words of its own.
export const SHARED_CHAT_TITLE = "Ask Studio";
export const SHARED_CHAT_DESCRIPTION =
  "Ask about this simulation to get targeted coaching.";

/**
 * What a Sabine chat is about, as the page holds it. AIChat takes one
 * `subject` and derives from it both the context a vote is filed with and the
 * body a shared copy is created from, so a page names its record once. The
 * result, transcript and readout pages each restated it in a share config and
 * a feedback context until October 2026, and the two had already drifted on
 * which studio a readout belonged to.
 *
 * Two schemas both call their type column `contextType`: the feedback rows'
 * says what the chat is about (`contextType` here); the shared thread's says
 * which table the record is in (`kind` here).
 *
 * @typedef {object} ChatSubject
 * @property {string} source The AIChatFeedbackSource the chat's route records
 *   every turn under; a vote updates that row, so this must match the route.
 * @property {string} [contextType] What the feedback rows say the chat is
 *   about: the scenario type, "TRANSCRIPT", "DEAL_DIAGNOSIS", "HOME_CHAT",
 *   "REPORTS_CHAT". For a record it is also the row's resultType.
 * @property {string} [workspaceId] The studio the chat ran in.
 * @property {string} [kind] Which table the record is in, as the shared
 *   thread's contextType spells it (SIMULATION_RESULT, TRANSCRIPT_RESULT,
 *   COG_REPORT). Only a chat about a record can be shared; the home and
 *   reports chats have no kind.
 * @property {number|string} [id] The record's id.
 * @property {string} [title] The record's name on the shared page.
 * @property {string} [label] What kind of record it is, in words
 *   ("Transcript result").
 * @property {string} [sharedTitle] The shared page's panel heading. This chat
 *   never shows it; its own header has no title.
 * @property {string} [sharedDescription] The line under that heading.
 */

/**
 * What a vote is filed with: the record's id and type as the feedback row's
 * contextId, contextType, resultId and resultType, plus the studio. A chat
 * about nothing in particular files only its context and studio.
 *
 * @param {ChatSubject} subject
 */
export function chatFeedbackContext({ kind, id, contextType, workspaceId } = {}) {
  const context = { contextType, workspaceId: workspaceId || undefined };
  if (!kind) return context;
  const resultId = id ? String(id) : undefined;
  return { contextId: resultId, ...context, resultId, resultType: contextType };
}

/**
 * The body AIChat posts to /api/chats/share: the record, the saved thread the
 * copy seeds from, the shared page's copy, and the thread as shown. The server
 * reads the record itself for the copy to coach from; `resultType` is what it
 * needs to find a practice result.
 *
 * @param {ChatSubject} subject
 * @param {{ summaryChips?: Array, messages: Array, contextPath: string }} thread
 */
export function sharedChatPayload(subject, { summaryChips, messages, contextPath }) {
  const sessionSource = getSabineSessionSource(subject.kind);
  if (!sessionSource) {
    throw new Error(`No saved chat source for a ${subject.kind} subject.`);
  }
  return {
    contextType: subject.kind,
    contextId: subject.id,
    workspaceId: subject.workspaceId,
    contextTitle: subject.title,
    contextLabel: subject.label,
    sessionSource,
    sessionContextId: subject.id,
    headerTitle: subject.sharedTitle || SHARED_CHAT_TITLE,
    headerDescription: subject.sharedDescription || SHARED_CHAT_DESCRIPTION,
    resultType: subject.contextType,
    summaryChips,
    messages,
    contextPath,
  };
}
